import { Observable } from 'rxjs';
import { singletonFactory } from '@warehouse/shared/util';
import {
	AssignTitlesParams,
	CreateFolderParams,
	CreateFolderResponse,
	Filters,
	InfraResourceIdentifier,
	NxObject,
	NxObjectFull,
	NxObjectRepository,
	SearchMode,
	WatchGetNxObjectResult,
	WatchNxObjectParams,
	WatchNxObjectsParams,
	WatchTreeParams,
} from '@warehouse/object-browser/core';
import { PaginatedDocuments } from '@warehouse/shared/core';
import { inventoryFetchClientAdapter } from './nx-object-fetch-client';
import { NxObjectAdapter } from './nx-object.adapter';
import { NxObjectFiltersBuilder } from './nx-object-filters-builder';
import { NxObjectHashBuilder } from './nx-object-hash-builder';
import { WildCardSearchAdapter } from './wild-card-search.adapter';
import { NxObjectFullAdapter } from './nx-object-full.adapter';
import { OrderByAdapter } from './order-by.adapter';

export class NxObjectRepositoryHttp implements NxObjectRepository {
	#inventoryFetchClientAdapter = inventoryFetchClientAdapter;

	watchNxObject({ nxObjectUuid, options }: WatchNxObjectParams): Observable<WatchGetNxObjectResult> {
		return this.#inventoryFetchClientAdapter.watchObservableQuery({
			queryKey: NxObjectHashBuilder.createGetQueryHash({ nxObjectUuid }),
			queryFn: () => this.#getNxObject({ nxObjectUuid }),
			refetchInterval: options?.pollInterval,
		});
	}

	watchNxObjects({
		filters = { pagination: { page: 1, perPage: 50 } },
		options,
		resourceIdentifier,
		searchMode,
		assignedTitleUuid,
	}: WatchNxObjectsParams) {
		return this.#inventoryFetchClientAdapter.watchObservableQuery({
			queryKey: NxObjectHashBuilder.createSearchQueryHash({
				filters,
				resourceIdentifier,
				searchMode,
				assignedTitleUuid,
			}),
			queryFn: () => this.#searchNxObject({ filters, resourceIdentifier, searchMode, assignedTitleUuid }),
			refetchInterval: options?.pollInterval,
		});
	}

	async assignTitles({ nxObjectUuid, titleUuids }: AssignTitlesParams): Promise<NxObjectFull> {
		const response = await this.#inventoryFetchClientAdapter.client.PUT('/nx-object/{uuid}/assign-titles', {
			params: {
				path: {
					uuid: nxObjectUuid,
				},
			},
			body: {
				titleUuids,
			},
		});

		if (response.error) throw new Error('Error assigning titles');
		return NxObjectFullAdapter.adapt(response.data);
	}

	async createFolder({ folderName, bucket, parentPath }: CreateFolderParams): Promise<CreateFolderResponse> {
		const { error, data } = await this.#inventoryFetchClientAdapter.client.POST('/nx-objects/create-folder', {
			body: {
				folderName,
				bucket,
				...(parentPath && { parentPath }),
			},
		});

		if (error) {
			const errorMessage = error?.message || 'Error creating folder';
			throw new Error(errorMessage);
		}
		return data as CreateFolderResponse;
	}

	async #getNxObject({ nxObjectUuid }: { nxObjectUuid: string }): Promise<NxObjectFull> {
		const { error, data } = await this.#inventoryFetchClientAdapter.client.GET('/nx-object/{uuid}', {
			params: {
				path: {
					uuid: nxObjectUuid,
				},
			},
		});
		if (error) throw new Error('Error fetch nx object');

		return NxObjectFullAdapter.adapt(data);
	}

	async #searchNxObject({
		filters = { pagination: { page: 1, perPage: 50 } },
		resourceIdentifier,
		searchMode,
		assignedTitleUuid,
	}: {
		filters?: Filters;
		resourceIdentifier?: InfraResourceIdentifier;
		searchMode?: SearchMode;
		assignedTitleUuid: string | undefined;
	}): Promise<PaginatedDocuments<NxObject>> {
		const { error, data } = await this.#inventoryFetchClientAdapter.client.POST('/nx-objects/search', {
			body: {
				orderBy: OrderByAdapter.adapt(filters.sort),
				pagination: filters.pagination,
				filters: NxObjectFiltersBuilder.buildFilters({
					filters: filters.filter,
					resourceIdentifier,
					searchMode,
					assignedTitleUuid,
				}),
				wildcardSearch: WildCardSearchAdapter.adapt(filters.textSearch),
			},
		});

		if (error) throw new Error('Error fetching nx objects');

		return {
			documents: NxObjectAdapter.adaptArray(data.documents),
			pagination: data.page,
		};
	}

	watchTree({ resourceIdentifier, options }: WatchTreeParams) {
		return this.watchNxObjects({
			filters: {
				filter: {
					combinationOperator: 'AND',
					filters: [
						{ field: 'itemType', operator: 'equals', value: 'folder' },
						{ field: 'online', operator: 'equals', value: true },
					],
				},
				sort: [{ field: 'name', sort: 'asc' }],
				pagination: {
					page: 1,
					perPage: 5000,
				},
			},
			options,
			resourceIdentifier,
		});
	}
}

export const nxObjectRepositorySingleton = singletonFactory<NxObjectRepository>({
	factory: () => new NxObjectRepositoryHttp(),
});
